import React, { useState, useRef, useEffect } from 'react';

const LazyImage = ({
  src,
  alt = '',
  width,
  height,
  className = '',
  id = '',
  style = {},
  onClick = null,
  useWebP = true // Opción para habilitar/deshabilitar WebP
}) => {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(null);
  const [error, setError] = useState(false);
  const imgRef = useRef();

  // Función para convertir la ruta a WebP
  const getWebPSrc = (originalSrc) => {
    if (!originalSrc || !useWebP) return originalSrc;

    // Si es una URL externa (comienza con http), retornar tal cual
    if (originalSrc.startsWith('http')) {
      return originalSrc;
    }

    // Para imágenes locales, intentar reemplazar la extensión por .webp
    const lastDotIndex = originalSrc.lastIndexOf('.');
    if (lastDotIndex === -1) return originalSrc;

    const withoutExtension = originalSrc.substring(0, lastDotIndex);
    return `${withoutExtension}.webp`;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.01, rootMargin: '50px' } // Cargar un poco antes de que sea visible
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Cuando la imagen entra en vista, determinar qué src usar
  useEffect(() => {
    if (inView && src) {
      if (useWebP) {
        // Intentar WebP primero
        const webpSrc = getWebPSrc(src);
        setCurrentSrc(webpSrc);
      } else {
        setCurrentSrc(src);
      }
    }
  }, [inView, src, useWebP]);

  const handleError = () => {
    // Si falla WebP, intentar con la imagen original
    if (currentSrc !== src && !error) {
      setCurrentSrc(src);
      setError(false);
    } else {
      setError(true);
    }
  };

  const handleLoad = () => {
    setLoaded(true);
  };

  return (
    <div
      ref={imgRef}
      className={`lazy-image-container ${loaded ? 'loaded' : ''} ${className}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#f0f0f0',
        ...style
      }}
    >
      {inView && currentSrc && !error && (
        <img
          src={currentSrc}
          alt={alt}
          width={width}
          height={height}
          id={id}
          className={className}
          style={{
            ...style,
            opacity: loaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out'
          }}
          onLoad={handleLoad}
          onError={handleError}
          loading="lazy"
          onClick={onClick}
        />
      )}
      {!loaded && inView && !error && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #ddd',
            borderTop: '3px solid #333',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
        </div>
      )}
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: '#999'
        }}>
          Imagen no disponible
        </div>
      )}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LazyImage;